import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const appendContractFormFields = (data, formData, { includeStatus = false } = {}) => {
  if (includeStatus && formData.status != null && String(formData.status).trim() !== "") {
    data.append("status", String(formData.status));
  }
  if (formData.business_sector_id) data.append("business_sector_id", formData.business_sector_id);
  if (formData.contract_type) data.append("contract_type", formData.contract_type);
  if (formData.contract_date) data.append("contract_date", formData.contract_date);
  if (formData.expiry_date) data.append("expiry_date", formData.expiry_date);
  if (formData.auto_renewal !== undefined && formData.auto_renewal !== null && formData.auto_renewal !== "") {
    data.append("auto_renewal", String(formData.auto_renewal));
  }
  if (formData.followup_enabled !== undefined && formData.followup_enabled !== null && formData.followup_enabled !== "") {
    data.append("followup_enabled", String(formData.followup_enabled));
  }
  if (formData.reminder_enabled !== undefined && formData.reminder_enabled !== null && formData.reminder_enabled !== "") {
    data.append("reminder_enabled", String(formData.reminder_enabled));
  }
  if (formData.reminder_days_before) data.append("reminder_days_before", formData.reminder_days_before);
  if (formData.renewal_period_months) data.append("renewal_period_months", formData.renewal_period_months);
  if (formData.business_sector_contract_id) {
    data.append("business_sector_contract_id", formData.business_sector_contract_id);
  }
  if (formData.winch_partner_contract_id) {
    data.append("winch_partner_contract_id", formData.winch_partner_contract_id);
  }
  if (formData.file) data.append("file", formData.file);
};

const appendElectronicContractFormFields = (data, formData) => {
  if (formData.business_sector_id) data.append("business_sector_id", formData.business_sector_id);
  if (formData.contract_type) data.append("contract_type", formData.contract_type);
  if (formData.contract_date) data.append("contract_date", formData.contract_date);
  if (formData.expiry_date) data.append("expiry_date", formData.expiry_date);
  if (formData.auto_renewal !== undefined && formData.auto_renewal !== null && formData.auto_renewal !== "") {
    data.append("auto_renewal", String(formData.auto_renewal));
  }
  if (formData.followup_enabled !== undefined && formData.followup_enabled !== null && formData.followup_enabled !== "") {
    data.append("followup_enabled", String(formData.followup_enabled));
  }
  if (formData.reminder_enabled !== undefined && formData.reminder_enabled !== null && formData.reminder_enabled !== "") {
    data.append("reminder_enabled", String(formData.reminder_enabled));
  }
  if (formData.reminder_days_before) data.append("reminder_days_before", formData.reminder_days_before);
  if (formData.renewal_period_months) data.append("renewal_period_months", formData.renewal_period_months);
  if (formData.business_sector_contract_id) {
    data.append("business_sector_contract_id", formData.business_sector_contract_id);
  }
  if (formData.winch_partner_contract_id) {
    data.append("winch_partner_contract_id", formData.winch_partner_contract_id);
  }
  {
    const quotationIds = Array.isArray(formData.quotation_request_customer_ids)
      ? formData.quotation_request_customer_ids
      : formData.quotation_request_customer_id
        ? [formData.quotation_request_customer_id]
        : [];
    quotationIds.forEach((id, index) => {
      if (id !== "" && id != null && id !== undefined) {
        data.append(`quotation_request_customer_ids[${index}]`, String(id));
      }
    });
  }
  // Always send on create/update so edit mode can change trips ↔ rentals.
  data.append("service_scope", formData.service_scope || "");
  if (formData.payment_method) data.append("payment_method", formData.payment_method);
  if (formData.payment_method === "partial") {
    data.append(
      "advance_payment_percentage",
      formData.advance_payment_percentage != null
        ? String(formData.advance_payment_percentage)
        : ""
    );
  }
  if (formData.invoice_issue_method) data.append("invoice_issue_method", formData.invoice_issue_method);
  if (formData.payment_method_after_receiving) {
    data.append("payment_method_after_receiving", formData.payment_method_after_receiving);
  }
  // Always send so edit can clear/update the value.
  data.append(
    "overnight_value",
    formData.overnight_value != null ? String(formData.overnight_value) : ""
  );
  data.append(
    "extra_unloading_location_value",
    formData.extra_unloading_location_value != null
      ? String(formData.extra_unloading_location_value)
      : ""
  );
  if (formData.custom_invoice_issue_method) {
    data.append("custom_invoice_issue_method", formData.custom_invoice_issue_method);
  }
  if (formData.custom_payment_method_after_receiving) {
    data.append("custom_payment_method_after_receiving", formData.custom_payment_method_after_receiving);
  }
};

// all business sector contracts
export const fetchBusinessSectorContracts = createAsyncThunk(
  "business_sector_contracts/fetchAll",
  async (arg, { rejectWithValue }) => {
    const params =
      arg && typeof arg === "object" && !Array.isArray(arg)
        ? arg
        : { page: Number(arg) || 1 };
    try {
      const response = await axios.get(`${BASE_URL}/business-sector-contracts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
        params,
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// Add business sector contract
export const addBusinessSectorContract = createAsyncThunk(
  "business_sector_contracts/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      appendContractFormFields(data, formData);

      const response = await axios.post(`${BASE_URL}/business-sector-contracts`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add contract";
      return rejectWithValue(message);
    }
  }
);

// Create electronic contract (setup step before editor)
export const createElectronicContract = createAsyncThunk(
  "business_sector_contracts/createElectronic",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      appendElectronicContractFormFields(data, formData);

      const response = await axios.post(
        `${BASE_URL}/business-sector-contracts/contract_electronic`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to create electronic contract";
      return rejectWithValue(message);
    }
  }
);

// Fetch electronic contract details (edit)
export const fetchElectronicContractDetails = createAsyncThunk(
  "business_sector_contracts/fetchElectronicDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/business-sector-contracts/${id}/electronic-details`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Lang: i18n.language,
          },
        }
      );
      return response.data?.data ?? response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load electronic contract details";
      return rejectWithValue(message);
    }
  }
);

// Update existing electronic contract (setup / metadata)
export const updateElectronicContract = createAsyncThunk(
  "business_sector_contracts/updateElectronic",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      appendElectronicContractFormFields(data, formData);

      const response = await axios.post(
        `${BASE_URL}/business-sector-contracts/${id}/contract_electronic`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update electronic contract";
      return rejectWithValue(message);
    }
  }
);

/** Persist full electronic contract document text as JSON */
export const saveElectronicContractDetailsJson = createAsyncThunk(
  "business_sector_contracts/saveContractDetailsJson",
  async ({ id, contractDetails }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      const payload =
        typeof contractDetails === "string"
          ? contractDetails
          : JSON.stringify(contractDetails ?? {});
      // API key spelling (contract_detials_json) is intentional.
      formData.append("contract_detials_json", payload);

      const response = await axios.post(
        `${BASE_URL}/business-sector-contracts/${id}/contract-details-json`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to save electronic contract details JSON";
      return rejectWithValue(message);
    }
  }
);

// Fetch business sectors for dropdown
export const fetchBusinessSectorsForContracts = createAsyncThunk(
  "business_sector_contracts/fetchBusinessSectors",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/business-sectors`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      // Handle different response structures
      if (response.data?.data?.data) {
        return response.data.data.data;
      } else if (response.data?.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      }
      return [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load business sectors";
      return rejectWithValue(message);
    }
  }
);

// Fetch winch partner contracts for dropdown
export const fetchWinchPartnerContracts = createAsyncThunk(
  "business_sector_contracts/fetchWinchPartnerContracts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/partner-contracts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      // Handle different response structures
      if (response.data?.data?.data) {
        return response.data.data.data;
      } else if (response.data?.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      }
      return [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partner contracts";
      return rejectWithValue(message);
    }
  }
);

// Fetch existing business sector contracts for dropdown (for Contract Addendum)
export const fetchBusinessSectorContractsForDropdown = createAsyncThunk(
  "business_sector_contracts/fetchContractsForDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/business-sector-contracts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      // Handle different response structures
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) {
          return response.data.data;
        } else if (response.data.data?.data) {
          return response.data.data.data;
        }
      }
      return [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load contracts";
      return rejectWithValue(message);
    }
  }
);

// Single contract details
export const fetchBusinessSectorContractDetail = createAsyncThunk(
  "business_sector_contracts/fetchDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/business-sector-contracts/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load contract details";
      return rejectWithValue(message);
    }
  }
);

// Update business sector contract
export const updateBusinessSectorContract = createAsyncThunk(
  "business_sector_contracts/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      appendContractFormFields(data, formData, { includeStatus: true });

      const response = await axios.post(`${BASE_URL}/business-sector-contracts/${id}`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update contract";
      return rejectWithValue(message);
    }
  }
);

// Update contract status
export const updateContractStatus = createAsyncThunk(
  "business_sector_contracts/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("status", status);

      const response = await axios.post(`${BASE_URL}/business-sector-contracts/${id}/status`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return { id, status, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update contract status";
      return rejectWithValue(message);
    }
  }
);

const BusinessSectorContractsSlice = createSlice({
  name: "business_sector_contracts",
  initialState: {
    business_sector_contracts: [],
    isLoading: false,
    error: null,
    success: null,
    pagination: null,
    businessSectors: [],
    winchPartnerContracts: [],
    contractsForDropdown: [],
    contractDetail: null,
    electronicContractDetail: null,
    detailLoading: false,
    electronicDetailLoading: false,
    isSaving: false,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
    clearContractDetail: (state) => {
      state.contractDetail = null;
    },
    clearElectronicContractDetail: (state) => {
      state.electronicContractDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessSectorContracts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessSectorContracts.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle response structure: { status: true, message: "...", data: [...] }
        if (action.payload?.data) {
          if (Array.isArray(action.payload.data)) {
            // Direct array response: { data: [...] }
            state.business_sector_contracts = action.payload.data;
            state.pagination = null;
          } else if (action.payload.data?.data) {
            // Paginated response: { data: { data: [...], pagination: {...} } }
            state.business_sector_contracts = action.payload.data.data;
            state.pagination = action.payload.data.pagination;
          } else {
            state.business_sector_contracts = [];
            state.pagination = null;
          }
        } else {
          state.business_sector_contracts = [];
          state.pagination = null;
        }
      })
      .addCase(fetchBusinessSectorContracts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Add contract
      .addCase(addBusinessSectorContract.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addBusinessSectorContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Contract added successfully";
      })
      .addCase(addBusinessSectorContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add contract";
      })
      .addCase(createElectronicContract.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createElectronicContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Electronic contract saved";
      })
      .addCase(createElectronicContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create electronic contract";
      })
      .addCase(fetchElectronicContractDetails.pending, (state) => {
        state.electronicDetailLoading = true;
        state.electronicContractDetail = null;
        state.error = null;
      })
      .addCase(fetchElectronicContractDetails.fulfilled, (state, action) => {
        state.electronicDetailLoading = false;
        state.electronicContractDetail = action.payload || null;
      })
      .addCase(fetchElectronicContractDetails.rejected, (state, action) => {
        state.electronicDetailLoading = false;
        state.electronicContractDetail = null;
        state.error = action.payload || "Failed to load electronic contract details";
      })
      .addCase(updateElectronicContract.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateElectronicContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Electronic contract updated";
      })
      .addCase(updateElectronicContract.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update electronic contract";
      })
      .addCase(saveElectronicContractDetailsJson.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(saveElectronicContractDetailsJson.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(saveElectronicContractDetailsJson.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(fetchBusinessSectorContractDetail.pending, (state) => {
        state.detailLoading = true;
        state.contractDetail = null;
        state.error = null;
      })
      .addCase(fetchBusinessSectorContractDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.contractDetail = action.payload || null;
      })
      .addCase(fetchBusinessSectorContractDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.contractDetail = null;
        state.error = action.payload || "Failed to load contract details";
      })
      .addCase(updateBusinessSectorContract.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateBusinessSectorContract.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = action.payload?.message || "Contract updated successfully";
      })
      .addCase(updateBusinessSectorContract.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || "Failed to update contract";
      })
      // Fetch business sectors
      .addCase(fetchBusinessSectorsForContracts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBusinessSectorsForContracts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businessSectors = action.payload || [];
      })
      .addCase(fetchBusinessSectorsForContracts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load business sectors";
      })
      // Fetch winch partner contracts
      .addCase(fetchWinchPartnerContracts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWinchPartnerContracts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.winchPartnerContracts = action.payload || [];
      })
      .addCase(fetchWinchPartnerContracts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load winch partner contracts";
      })
      // Fetch contracts for dropdown
      .addCase(fetchBusinessSectorContractsForDropdown.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBusinessSectorContractsForDropdown.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contractsForDropdown = action.payload || [];
      })
      .addCase(fetchBusinessSectorContractsForDropdown.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load contracts";
      })
      // Update contract status
      .addCase(updateContractStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateContractStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.data?.message || "Status updated successfully";
        // Update the contract status in the list
        const contractIndex = state.business_sector_contracts.findIndex(
          (contract) => contract.id === action.payload.id
        );
        if (contractIndex !== -1) {
          state.business_sector_contracts[contractIndex].status = action.payload.status;
        }
      })
      .addCase(updateContractStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update contract status";
      });
  },
});

export const { clearState, clearContractDetail, clearElectronicContractDetail } =
  BusinessSectorContractsSlice.actions;
export default BusinessSectorContractsSlice.reducer;
